import { useQuery } from '@tanstack/react-query';
import * as analyticsApi from '../Api/analyticsApi';
import * as campaignApi from '../Api/campaignApi';
import * as employeeApi from '../Api/employeeApi';
import * as companyApi from '../Api/companyApi';
import * as listApi from '../Api/listApi';
import * as userApi from '../Api/userApi';
import * as projectApi from '../Api/projectApi';
import * as invoiceApi from '../Api/invoiceApi';
import * as notificationApi from '../Api/notificationApi';
import { getLeads } from '../services/CRM/leadsService';
import {
	isTodoCompletedStatus,
	normalizeWorkItemStatus,
} from '../components/TodoTask/WorkspaceViews/taskViewUtils';

const DASHBOARD_KEY = 'dashboard';

const unwrapList = (payload) => {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.projects)) return payload.projects;
	if (Array.isArray(payload?.data)) return payload.data;
	return [];
};

const safeRequest = async (request, fallback) => {
	try {
		return await request();
	} catch {
		return fallback;
	}
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const isLeadClosed = (status = '') => {
	const normalized = String(status).toLowerCase();
	return ['won', 'lost', 'closed'].includes(normalized);
};

const isInvoicePaid = (status = '') =>
	String(status || '').toLowerCase() === 'paid';

export const useDashboardData = (companyId) => {
	return useQuery({
		queryKey: [DASHBOARD_KEY, companyId],
		queryFn: async () => {
			if (!companyId) return null;

			const [
				analyticsResponse,
				campaignsResponse,
				employeesResponse,
				companyResponse,
				notificationsResponse,
				userResponse,
				listsResponse,
				projectsResponse,
				leadsData,
				invoicesData,
			] = await Promise.all([
				analyticsApi.getAnalytics(),
				campaignApi.getCampaigns(companyId),
				employeeApi.getEmployees(),
				companyApi.getCompanyById(companyId),
				notificationApi.fetchNotifications(companyId),
				userApi.getUserDetails(),
				listApi.getLists(companyId),
				safeRequest(
					() => projectApi.getCompanyProjects(companyId),
					{ data: [] },
				),
				safeRequest(() => getLeads(companyId), []),
				safeRequest(() => invoiceApi.getInvoices(companyId), { data: [] }),
			]);

			const analytics = analyticsResponse.data;
			const campaigns = campaignsResponse.data || [];
			const employees = employeesResponse.data || [];
			const company = companyResponse.data;
			const notifications = notificationsResponse || [];
			const user = userResponse.data;
			const lists = listsResponse.data || [];
			const projects = unwrapList(projectsResponse?.data ?? projectsResponse);
			const leads = Array.isArray(leadsData) ? leadsData : [];
			const invoices = invoicesData?.data || invoicesData || [];

			const totalContactsFromLists =
				lists?.reduce((sum, list) => sum + (list.totalContacts || 0), 0) || 0;

			const activeCampaigns =
				campaigns?.filter(
					(c) => c.status === 'active' || c.status === 'sending',
				)?.length || 0;

			const activeProjects =
				projects?.filter((project) => {
					const status = normalizeWorkItemStatus(
						project?.generalInfo?.status || 'not started',
					);
					return !isTodoCompletedStatus(status);
				})?.length || 0;

			const openLeads =
				leads?.filter((lead) => !isLeadClosed(lead?.status))?.length || 0;

			const unpaidInvoices =
				invoices?.filter((invoice) => !isInvoicePaid(invoice?.paymentStatus))
					?.length || 0;

			const openRate =
				analytics?.totalOpens && analytics?.totalEmailsDelivered
					? (analytics.totalOpens / analytics.totalEmailsDelivered) * 100
					: 0;

			const clickRate =
				analytics?.totalClicks && analytics?.totalEmailsDelivered
					? (analytics.totalClicks / analytics.totalEmailsDelivered) * 100
					: 0;

			const companyStats = {
				totalEmployees: employees?.length || 0,
				activeCampaigns,
				totalCampaigns: campaigns?.length || 0,
				totalContacts: totalContactsFromLists,
				emailsSent: analytics?.totalEmailsDelivered || 0,
				openRate,
				clickRate,
				totalProjects: projects?.length || 0,
				activeProjects,
				totalLeads: leads?.length || 0,
				openLeads,
				totalInvoices: invoices?.length || 0,
				unpaidInvoices,
				unreadNotifications:
					notifications?.filter((notification) => !notification.isRead)?.length ||
					0,
				contactLists: lists?.length || 0,
				bounces: analytics?.totalBounces || 0,
			};

			const mapCampaignSummary = (campaign) => {
				const campaignAnalytics = analytics?.campaigns?.find(
					(entry) => entry._id === campaign._id,
				);

				return {
					id: campaign._id,
					name: campaign.name,
					subject: campaign.subject,
					status: campaign.status,
					sent: campaign.sentRecipients?.length || 0,
					recipients: campaign.recipients?.length || 0,
					opens: campaignAnalytics?.opens || 0,
					clicks: campaignAnalytics?.clicks || 0,
					createdAt: campaign.createdAt,
					created: campaign.createdAt
						? new Date(campaign.createdAt).toLocaleDateString()
						: '—',
				};
			};

			const campaignSummaries =
				campaigns
					?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
					?.map(mapCampaignSummary) || [];

			const recentCampaigns = campaignSummaries.slice(0, 6);

			const recentProjects =
				projects
					?.sort(
						(a, b) =>
							new Date(b.updatedAt || b.createdAt) -
							new Date(a.updatedAt || a.createdAt),
					)
					?.slice(0, 5)
					?.map((project) => ({
						id: project._id,
						title:
							project.generalInfo?.title || project.name || 'Untitled project',
						code: project.generalInfo?.projectCode || '—',
						status: normalizeWorkItemStatus(
							project.generalInfo?.status || 'not started',
						),
						client: project.generalInfo?.clientOwnsThisProject?.companyName,
						updatedAt: project.updatedAt || project.createdAt,
					})) || [];

			const recentLeads =
				leads
					?.sort(
						(a, b) =>
							new Date(b.updatedAt || b.createdAt) -
							new Date(a.updatedAt || a.createdAt),
					)
					?.slice(0, 5)
					?.map((lead) => ({
						id: lead._id,
						name:
							lead.name ||
							`${lead.firstName || ''} ${lead.lastName || ''}`.trim() ||
							'Unnamed lead',
						company: lead.companyName || lead.company,
						status: lead.status || 'new',
						updatedAt: lead.updatedAt || lead.createdAt,
					})) || [];

			const teamMembers =
				employees?.map((employee) => ({
					id: employee._id,
					name: `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
					email: employee.email,
					systemRole: employee.role || 'Employee',
					role:
						employee.employmentDetails?.[0]?.position ||
						employee.role ||
						'Employee',
					department: employee.employmentDetails?.[0]?.department,
					joinedAt: employee.createdAt,
					avatar: employee.profileImage,
				})) || [];

			const companyAlerts =
				notifications
					?.filter((notification) => !notification.isRead)
					?.slice(0, 6)
					?.map((notification) => ({
						id: notification._id,
						type: notification.type || 'info',
						title: notification.title || 'Notification',
						message: notification.message,
						timestamp: notification.createdAt,
						isRead: notification.isRead,
					})) || [];

			return {
				companyStats,
				campaignSummaries,
				recentCampaigns,
				recentProjects,
				recentLeads,
				teamMembers,
				companyAlerts,
				analytics,
				campaigns,
				employees,
				company,
				notifications,
				user,
				lists,
				projects,
				leads,
				invoices,
				formattedStats: {
					openRate: formatPercent(openRate),
					clickRate: formatPercent(clickRate),
				},
			};
		},
		enabled: !!companyId,
	});
};
